/**
 * `<caos-nav-gadget>` — the CUSTOMIZE NAVIGATION control at the FAR END of the workspace tab row
 * (Dragon parity: the little sliders/adjustments button after the tabs). Clicking it opens the
 * "Customize Navigation" popup DIRECTLY — there is no intermediate search dropdown and no nested
 * duplicate button (CAOS-293). It lets the user reorder, hide, and add the active app's tabs; the
 * change is applied to THEIR view (per-user, non-default apps only).
 * (Catalog: Navigation › NavGadget — the Customize Navigation control at the far end of the tab row.)
 *
 * Trigger: JUST a sliders/adjustments glyph in the `.os-cmd-trigger` shape (bordered, subtle bg,
 * accent icon) — the same idiom as the App Launcher waffle, so the two ends of the row read as a pair.
 *
 * Popup (portaled to `document.body` under its own shadow host so the tab row's overflow can never
 * clip it; `position: fixed`, anchored under the trigger):
 *   • Title "Customize Navigation" + a subtitle naming the app.
 *   • CURRENT NAVIGATION — one row per visible tab, each with move-up / move-down / hide (eye-off).
 *   • AVAILABLE ITEMS — a "Search items to add…" filter + one row per addable item with "+ Add".
 *   • Footer — "Reset to Default" + "Done".
 *
 * Data in (properties — arrays cannot ride attributes):
 *   • `items`     — the current navigation `{ id, label, href, active? }[]` (order = display order).
 *   • `available` — the addable items `{ id, label, href }[]` (optional; hidden tabs are added here).
 *   • `defaults`  — CAOS-580: the app's PUBLISHED navigation, i.e. what "Reset to Default" restores.
 *     The host sets it on every repaint so it always describes the app on screen.
 * Attribute in:
 *   • `app-label` — the active app's name, woven into the subtitle.
 *
 * Events out (bubbling + composed — the host owns navigation + persistence):
 *   • `gadget-nav-change` — detail `{ items }`, the new VISIBLE navigation (ordered) after a
 *     reorder / hide / add. The host applies it to the live strip and remembers it.
 *   • `gadget-nav-reset`  — the user chose "Reset to Default". The host forgets the arrangement and
 *     repaints; this event does NOT come with a change event, because a reset withdraws a
 *     preference rather than making a new one.
 *
 * This control holds NO durable state of its own. Where an arrangement is kept, and for how long, is
 * the host's business (CAOS-580/CAOS-470: `host/src/nav-preferences.ts`, kept against the person) —
 * which is why the subtitle can say it is saved to the profile.
 */
import { CaosElement, RESET, define, makeSheet } from '../base-element.js';
import { panelFocus, type PanelFocus } from '../focus.js';
import { anchorPanel } from '../anchored-panel.js';
import { makeIcon } from './icons.js';

export interface CaosNavItem {
  id: string;
  label: string;
  href: string;
  active?: boolean;
}

/**
 * CAOS-602 — these four marks used to be drawn in this file.
 *
 * The sliders glyph carried a note saying to keep its path in lock-step with the gold standard,
 * which is exactly the instruction a private copy cannot honour: it asks a reader of THIS file to
 * remember a fact about a drawing kept somewhere else. In the one vocabulary it is the same
 * geometry under a name, and the note travels with the path rather than with this component.
 *
 * The two chevrons were the plainer case. This file drew an up and a down because the vocabulary
 * offered a single `chevron`, so the cheapest way to get the other direction was to draw it.
 */
const slidersIcon = (): SVGSVGElement => makeIcon('sliders-horizontal');
const chevronUp = (): SVGSVGElement => makeIcon('chevron-up');
const chevronDown = (): SVGSVGElement => makeIcon('chevron');
const eyeOff = (): SVGSVGElement => makeIcon('eye-off');

const sheet = makeSheet(`
  :host { display: inline-flex; align-items: center; }

  /* Trigger — Dragon's .os-cmd-trigger: bordered, subtle bg, tight radius; the icon is accent. */
  .trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 10px;
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-accent);
    line-height: 1;
    border-radius: var(--caos-radius-sm);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
  }
  .trigger:hover { border-color: var(--caos-color-border-strong); background: var(--caos-color-surface); }
  .trigger:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); border-color: var(--caos-color-accent); }
  .trigger svg { width: 16px; height: 16px; color: var(--caos-color-accent); flex-shrink: 0; }
`);

/** Shadow-root stylesheet for the portaled Customize Navigation popup (body-hosted, unclippable). */
const panelSheet = makeSheet(`
  .panel {
    position: fixed;
    /* A NAMED layer, not a number — see the header note on layering (CAOS-586). */
    z-index: var(--caos-layer-dropdown);
    width: 320px;
    max-height: 78vh;
    overflow-y: auto;
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius);
    box-shadow: var(--caos-shadow-float);
    font-family: var(--caos-font);
    color: var(--caos-color-text);
  }
  .panel[hidden] { display: none; }

  .head { padding: 14px 16px 10px; border-bottom: 1px solid var(--caos-color-border); }
  .title { font-size: 14px; font-weight: 600; color: var(--caos-color-text); margin: 0; }
  .sub { font-size: 12px; color: var(--caos-color-text-muted); margin: 4px 0 0; line-height: 1.4; }

  .section {
    font-family: var(--caos-font-mono);
    font-size: 9px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
    padding: 12px 16px 6px;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 7px 14px 7px 16px;
    box-sizing: border-box;
  }
  .row:hover { background: var(--caos-color-surface-2); }
  .row.is-active { box-shadow: inset 3px 0 0 var(--caos-color-accent); font-weight: 600; }
  .row__label { flex: 1; min-width: 0; font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .row__actions { display: inline-flex; gap: 2px; }

  .icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px; height: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: var(--caos-radius-sm);
    cursor: pointer;
    color: var(--caos-color-text-muted);
  }
  .icon-btn:hover:not([disabled]) { background: var(--caos-color-surface); border-color: var(--caos-color-border); color: var(--caos-color-text); }
  .icon-btn:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .icon-btn[disabled] { opacity: var(--caos-state-disabled-opacity); cursor: default; }
  .icon-btn svg { width: 15px; height: 15px; }

  .search-wrap { padding: 6px 16px 2px; }
  .search input {
    width: 100%;
    box-sizing: border-box;
    background: var(--caos-color-bg);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    padding: 7px 10px;
    color: var(--caos-color-text);
    font-family: inherit;
    font-size: 13px;
  }
  .search input::placeholder { color: var(--caos-color-text-ghost); }
  .search input:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); border-color: var(--caos-color-accent); }

  .add {
    font-family: var(--caos-font-mono);
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 5px 10px;
    background: transparent;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    cursor: pointer;
    color: var(--caos-color-accent);
  }
  .add:hover { border-color: var(--caos-color-accent); background: var(--caos-color-surface-2); }
  .add:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  .empty { padding: 10px 16px 12px; color: var(--caos-color-text-muted); font-size: 13px; }

  .footer {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    border-top: 1px solid var(--caos-color-border);
    padding: 10px 16px;
    position: sticky;
    bottom: 0;
    background: var(--caos-color-surface);
  }
  .btn {
    padding: 7px 14px;
    border-radius: var(--caos-radius-sm);
    font-family: var(--caos-font);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid var(--caos-color-border);
    background: transparent;
    color: var(--caos-color-text);
  }
  .btn:hover { background: var(--caos-color-surface-2); border-color: var(--caos-color-border-strong); }
  .btn:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  /* CAOS-652 — accent-FILL under the accent ink, as every other filled control has since CAOS-1551:
     white on the plain accent is 3.60:1, below AA, and the brightness filter on hover lightened it
     further. The ink was spelled through a token nothing defined, so the contrast check never saw it. */
  .btn--primary { background: var(--caos-color-accent-fill); border-color: var(--caos-color-accent-fill); color: var(--caos-color-accent-contrast); }
  .btn--primary:hover { background: var(--caos-color-accent-fill-hover); border-color: var(--caos-color-accent-fill-hover); }
`);

export class CaosNavGadget extends CaosElement {
  private _items: CaosNavItem[] = [];
  private _available: CaosNavItem[] = [];
  /** The host-declared published navigation for the app on screen (CAOS-580). */
  private _defaults: CaosNavItem[] | null = null;
  /** Fallback for a standalone control with no host: the first arrangement it was ever given. */
  private _firstItems: CaosNavItem[] | null = null;
  private _firstAvailable: CaosNavItem[] | null = null;
  private _availQuery = '';
  private open = false;

  private trigger!: HTMLButtonElement;
  private portalHost: HTMLDivElement | null = null;
  /** CAOS-651 — focus into the open panel, kept there, and handed back on close. */
  private panelFocus: PanelFocus | null = null;
  private portalRoot: ShadowRoot | null = null;
  private panel!: HTMLElement;
  private bodyEl!: HTMLElement;

  private readonly onDocPointer = (e: MouseEvent): void => {
    if (!this.open) return;
    const path = e.composedPath();
    if (path.includes(this)) return;
    if (this.portalHost && path.includes(this.portalHost)) return;
    this.setOpen(false);
  };
  private readonly onDocKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.setOpen(false, true);
    else this.panelFocus?.trap(e);
  };
  private readonly reposition = (): void => {
    if (!this.open) return;
    // CAOS-659 — under the trigger, lined up with its right edge; above it when there is no room
    // below; slid along to stay inside the visible viewport either way.
    anchorPanel(this.panel, this.trigger, { side: 'bottom', align: 'end', gap: 8 });
  };

  constructor() {
    super(sheet);
    this.trigger = document.createElement('button');
    this.trigger.type = 'button';
    this.trigger.className = 'trigger';
    this.trigger.setAttribute('aria-haspopup', 'dialog');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-label', 'Customize navigation');
    this.trigger.title = 'Customize Navigation';
    this.trigger.append(slidersIcon());
    this.trigger.addEventListener('click', () => this.setOpen(!this.open));
    this.root.append(this.trigger);
  }

  connectedCallback(): void {
    this.ensurePortal();
  }

  /**
   * Tear the control back down to CLOSED, not merely to portal-less.
   *
   * CAOS-580 — the workspace now removes this control when there is no navigation to customise and
   * re-appends it when there is, so being detached and re-attached is ordinary rather than terminal.
   * Leaving `open` set through that made the first press after coming back do nothing: the control
   * believed the popup was open while a freshly rebuilt, hidden panel said otherwise, so the press
   * computed "close" and the person saw nothing happen until they pressed again. Browser Back then
   * Forward is enough to reach it, because that route change rebuilds the frame with no click inside
   * the page to close the popup on the way.
   *
   * A control that does nothing when pressed is the exact defect this ticket removes, so the state
   * has to come down with the DOM.
   */
  disconnectedCallback(): void {
    document.removeEventListener('mousedown', this.onDocPointer);
    document.removeEventListener('keydown', this.onDocKey);
    window.removeEventListener('scroll', this.reposition, true);
    window.removeEventListener('resize', this.reposition);
    this.portalHost?.remove();
    this.portalHost = null;
    this.portalRoot = null;
    this.panelFocus = null;
    this.open = false;
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  /** The current navigation (data in — a property, so an array not an attribute). */
  get items(): CaosNavItem[] {
    return this._items;
  }
  set items(list: CaosNavItem[]) {
    this._items = Array.isArray(list) ? list.map((i) => ({ ...i })) : [];
    if (!this._firstItems) this._firstItems = this._items.map((i) => ({ ...i }));
    if (this.open) this.renderPanel();
  }

  /** The addable items (optional). Hidden tabs are also added here as they are hidden. */
  get available(): CaosNavItem[] {
    return this._available;
  }
  set available(list: CaosNavItem[]) {
    this._available = Array.isArray(list) ? list.map((i) => ({ ...i })) : [];
    // Capture the first available list INDEPENDENTLY of the first item list. Guarding this on
    // `_firstItems` never fired: the host sets `items` before `available` on every repaint, so the
    // item list was always populated by the time this ran and the fallback was never captured.
    if (this._firstAvailable === null) this._firstAvailable = this._available.map((i) => ({ ...i }));
    if (this.open) this.renderPanel();
  }

  /**
   * CAOS-580 — the app's PUBLISHED navigation, i.e. exactly what "Reset to Default" restores. The
   * host sets it on every repaint, so it always describes the app currently on screen.
   *
   * This control used to keep its own copy of the first list it was ever given and reset to that,
   * which meant that after switching app, Reset to Default restored the tabs of whichever app had
   * been opened first in the session. The default belongs to the host, which knows which app is on
   * screen; the control does not get to remember it.
   *
   * `defaults` is the FULL published navigation, so restoring it leaves nothing addable — resetting
   * clears the available list too.
   */
  get defaults(): CaosNavItem[] {
    return this._defaults ?? [];
  }
  set defaults(list: CaosNavItem[]) {
    this._defaults = Array.isArray(list) ? list.map((i) => ({ ...i })) : [];
  }

  private appLabel(): string {
    const l = this.getAttribute('app-label');
    return l && l.trim() ? l.trim() : 'this app';
  }

  private ensurePortal(): void {
    if (this.portalHost) return;
    this.portalHost = document.createElement('div');
    this.portalHost.className = 'caos-nav-gadget-portal';
    this.portalRoot = this.portalHost.attachShadow({ mode: 'open' });
    // RESET first, exactly as the base class does for the component's own root: this root is
    // outside that machinery, so without this line the shared hiding guard would not reach
    // anything inside the panel. See base-element.ts.
    this.portalRoot.adoptedStyleSheets = [RESET, panelSheet];

    this.panel = document.createElement('div');
    this.panel.className = 'panel';
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-label', 'Customize Navigation');
    this.panel.hidden = true;

    this.bodyEl = document.createElement('div');
    this.panel.append(this.bodyEl);
    this.portalRoot.append(this.panel);
    document.body.append(this.portalHost);
  }

  /** `restoreFocus` — Escape or Done hands focus back to the trigger; an outside click does not. */
  private setOpen(open: boolean, restoreFocus = false): void {
    if (open === this.open) return;
    this.open = open;
    this.ensurePortal();
    this.panel.hidden = !open;
    this.trigger.setAttribute('aria-expanded', String(open));
    if (open) {
      this._availQuery = '';
      this.renderPanel();
      this.reposition();
      document.addEventListener('mousedown', this.onDocPointer);
      document.addEventListener('keydown', this.onDocKey);
      window.addEventListener('scroll', this.reposition, true);
      window.addEventListener('resize', this.reposition);
      this.panelFocus = panelFocus(this.panel);
      this.panelFocus.open({ opener: this.trigger });
    } else {
      document.removeEventListener('mousedown', this.onDocPointer);
      document.removeEventListener('keydown', this.onDocKey);
      window.removeEventListener('scroll', this.reposition, true);
      window.removeEventListener('resize', this.reposition);
      this.panelFocus?.close(restoreFocus);
      this.panelFocus = null;
    }
  }

  private emit(type: string, detail?: unknown): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail }));
  }

  /** Emit the new VISIBLE navigation (ordered) so the host applies + persists it. */
  private emitChange(): void {
    this.emit('gadget-nav-change', { items: this._items.map((i) => ({ ...i })) });
  }

  private iconButton(icon: SVGSVGElement, label: string, disabled: boolean, onClick: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'icon-btn';
    b.setAttribute('aria-label', label);
    b.title = label;
    if (disabled) b.disabled = true;
    b.append(icon);
    if (!disabled) b.addEventListener('click', onClick);
    return b;
  }

  private moveItem(index: number, delta: number): void {
    const to = index + delta;
    if (to < 0 || to >= this._items.length) return;
    const [it] = this._items.splice(index, 1);
    this._items.splice(to, 0, it!);
    this.renderPanel();
    this.emitChange();
  }

  private hideItem(index: number): void {
    if (this._items.length <= 1) return; // never leave the tab row empty (see the Hide button above)
    const [it] = this._items.splice(index, 1);
    if (it) this._available.push({ id: it.id, label: it.label, href: it.href });
    this.renderPanel();
    this.emitChange();
  }

  private addItem(id: string): void {
    const idx = this._available.findIndex((a) => a.id === id);
    if (idx < 0) return;
    const [it] = this._available.splice(idx, 1);
    if (it) this._items.push({ ...it });
    this.renderPanel();
    this.emitChange();
  }

  /**
   * Restore the app on screen to its published navigation.
   *
   * It emits `gadget-nav-reset` and NOT `gadget-nav-change`: a reset is the person withdrawing their
   * arrangement, not making a new one. Emitting a change as well made the host store the restored
   * order right back as a customisation, so "reset" wrote a preference instead of clearing one.
   */
  private resetDefault(): void {
    this._availQuery = '';
    if (this._defaults) {
      this._items = this._defaults.map((i) => ({ ...i }));
      this._available = []; // defaults are the FULL published navigation — nothing left to add
    } else {
      // No host has declared the published navigation (a standalone control): fall back to the first
      // arrangement this control was given.
      this._items = (this._firstItems ?? []).map((i) => ({ ...i }));
      this._available = (this._firstAvailable ?? []).map((i) => ({ ...i }));
    }
    this.renderPanel();
    this.emit('gadget-nav-reset');
  }

  private renderPanel(): void {
    if (!this.portalRoot) return;
    this.bodyEl.replaceChildren();

    // Header.
    const head = document.createElement('div');
    head.className = 'head';
    const title = document.createElement('p');
    title.className = 'title';
    title.textContent = 'Customize Navigation';
    const sub = document.createElement('p');
    sub.className = 'sub';
    // CAOS-580 — say where the arrangement actually goes. While it was kept in the browser this read
    // "saved for you in this browser"; CAOS-470 keeps it against the person, so "saved to your profile"
    // is true again. The wording matches the Profile screen's own personalization note so the two never
    // disagree about what "saved" means.
    sub.textContent = `Reorder, hide, or add tabs to your ${this.appLabel()} navigation. Changes are saved to your profile and only affect your view.`;
    head.append(title, sub);
    this.bodyEl.append(head);

    // CURRENT NAVIGATION.
    const curLabel = document.createElement('div');
    curLabel.className = 'section';
    curLabel.textContent = 'Current Navigation';
    this.bodyEl.append(curLabel);

    this._items.forEach((it, i) => {
      const row = document.createElement('div');
      row.className = it.active ? 'row is-active' : 'row';
      const label = document.createElement('span');
      label.className = 'row__label';
      label.textContent = it.label;
      const actions = document.createElement('div');
      actions.className = 'row__actions';
      actions.append(
        this.iconButton(chevronUp(), 'Move up', i === 0, () => this.moveItem(i, -1)),
        this.iconButton(chevronDown(), 'Move down', i === this._items.length - 1, () => this.moveItem(i, 1)),
        // CAOS-580 — the LAST visible tab cannot be hidden. An empty tab row used to last until the
        // next load; now that the arrangement is remembered it would survive one, and a person who
        // hid everything would come back to a navigation with nothing in it and no obvious way out.
        // (The popup itself still opens — it is gated on what the APP publishes, not on what is
        // visible — so this is a courtesy, not the only way back. It is a cheap one.)
        this.iconButton(
          eyeOff(),
          this._items.length <= 1 ? 'Hide — keep at least one tab' : 'Hide',
          this._items.length <= 1,
          () => this.hideItem(i),
        ),
      );
      row.append(label, actions);
      this.bodyEl.append(row);
    });

    // AVAILABLE ITEMS.
    const availLabel = document.createElement('div');
    availLabel.className = 'section';
    availLabel.textContent = 'Available Items';
    this.bodyEl.append(availLabel);

    const searchWrap = document.createElement('div');
    searchWrap.className = 'search-wrap';
    const search = document.createElement('div');
    search.className = 'search';
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Search items to add…';
    input.value = this._availQuery;
    input.setAttribute('aria-label', 'Search items to add');
    input.addEventListener('input', () => {
      this._availQuery = input.value;
      this.renderAvailableList(availList, input);
    });
    search.append(input);
    searchWrap.append(search);
    this.bodyEl.append(searchWrap);

    const availList = document.createElement('div');
    this.bodyEl.append(availList);
    this.renderAvailableList(availList, null);

    // Footer.
    const footer = document.createElement('div');
    footer.className = 'footer';
    const reset = document.createElement('button');
    reset.type = 'button';
    reset.className = 'btn';
    reset.textContent = 'Reset to Default';
    reset.addEventListener('click', () => this.resetDefault());
    const done = document.createElement('button');
    done.type = 'button';
    done.className = 'btn btn--primary';
    done.textContent = 'Done';
    done.addEventListener('click', () => this.setOpen(false, true));
    footer.append(reset, done);
    this.bodyEl.append(footer);
  }

  /** Render (or re-render) just the available-items list against the current search query. */
  private renderAvailableList(host: HTMLElement, keepFocus: HTMLInputElement | null): void {
    host.replaceChildren();
    const q = this._availQuery.trim().toLowerCase();
    const matches = this._available.filter((a) => !q || a.label.toLowerCase().includes(q));
    if (matches.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'empty';
      empty.textContent = q ? `No items match “${this._availQuery}”.` : 'No items to add.';
      host.append(empty);
    } else {
      for (const a of matches) {
        const row = document.createElement('div');
        row.className = 'row';
        const label = document.createElement('span');
        label.className = 'row__label';
        label.textContent = a.label;
        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'add';
        add.textContent = '+ Add';
        add.setAttribute('aria-label', `Add ${a.label}`);
        add.addEventListener('click', () => this.addItem(a.id));
        row.append(label, add);
        host.append(row);
      }
    }
    if (keepFocus) keepFocus.focus();
  }
}

define('caos-nav-gadget', CaosNavGadget);

declare global {
  interface HTMLElementTagNameMap {
    'caos-nav-gadget': CaosNavGadget;
  }
}
